import React, { useState } from "react";
import { useIntl } from "react-intl";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { MaterialTooltip } from "src/app/components/MaterialTooltip/MaterialTooltip";
import { ReactComponent as ErrorIcon } from "src/images/post-check-alert.svg";
import { SelectedLabels } from "src/app/components/SelectTags/partials/SelectedLabels/SelectedLabels";
import {
  isValidUrl,
  generateCreatorFromString,
} from "src/app/modals/AddCreatorsModal/components/GenerateCreatorFromString";

export default function SearchByUsernameOrLink({
  isError,
  // New API (multi-username with tags)
  usernames,
  onAddUsername,
  onRemoveUsername,
  onClearUsernames,
  // Old API (single username input, old CDT search / creator lists)
  username,
  onUsernameChange,
  onKeyPress,
  onUsernamePaste,
}) {
  const intl = useIntl();
  const [inputValue, setInputValue] = useState("");

  const isMultiMode = usernames !== undefined;

  if (!isMultiMode) {
    // Legacy single-input mode (old CDT search / creator lists)
    return (
      <div className="audience-search__field" data-qa="filter_influencer_username">
        <div className="idh-label idh-label--with-tooltip">
          <IDHFormattedMessage
            id="front_search_by_social_login_topic"
            defaultMessage="Search by Social Login or Name"
          />
          <MaterialTooltip
            id="front_influencer_social_login_tooltip_1"
            content={
              <>
                <span className="audience-search__tooltip--white-bold">
                  <IDHFormattedMessage
                    id="front_influencer_social_login_tooltip_1"
                    defaultMessage="Here you can find a specific creator by entering his nickname from the selected platform."
                  />
                </span>
                <br />
                <br />
                <div>
                  <span className="audience-search__tooltip--bold">
                    <IDHFormattedMessage
                      id="front_influencer_social_login_tooltip_2"
                      defaultMessage="Important: "
                    />
                  </span>
                  <IDHFormattedMessage
                    id="front_influencer_social_login_tooltip_3"
                    defaultMessage="When searching for a creator by entering its nickname from a given platform, you cannot use the other filters."
                  />
                </div>
                <br />
                <br />
                <div>
                  <span
                    className="audience-search__tooltip--bold"
                    style={{ marginRight: "4px" }}
                  >
                    <IDHFormattedMessage
                      id="front_influencer_social_login_tooltip_4"
                      defaultMessage="Tip: "
                    />
                  </span>
                  <IDHFormattedMessage
                    id="front_influencer_social_login_tooltip_5"
                    defaultMessage='If you want to search for given username exactly, put it in quotes (")'
                  />
                </div>
              </>
            }
          />
        </div>
        <div
          className={`audience-search__input ${isError ? "audience-search__input--error" : ""}`}
        >
          <div className="cdt-search-input">
            <input
              placeholder={intl.formatMessage({
                id: "front_search_by_social_login_placeholder",
                defaultMessage: "Search by social login or name",
              })}
              value={username}
              onChange={onUsernameChange}
              onPaste={onUsernamePaste}
              onKeyPress={onKeyPress}
              data-cdt-input="influencer-username"
            />
          </div>
          {isError && (
            <div className="audience-search__input__error-message">
              <ErrorIcon />
              <IDHFormattedMessage
                id="front_incorrect_provider_error"
                defaultMessage="Incorrect link has been entered"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Multi-username mode (CreatorSearch)
  // Extract username from a URL or return the string as-is
  const extractUsernameFromString = (str) => {
    const trimmed = str.trim();
    if (!trimmed) return null;

    if (isValidUrl(trimmed)) {
      const generatedCreator = generateCreatorFromString(trimmed);
      return generatedCreator.username;
    }

    return trimmed;
  };

  const addUsernamesFromText = (text) => {
    // Split by newlines (both \n and \r\n), commas, and spaces
    const entries = text
      .split(/[\r\n,\s]+/)
      .map((entry) => extractUsernameFromString(entry))
      .filter((username) => username && username.length > 0);

    entries.forEach((username) => {
      onAddUsername(username);
    });
  };

  const handleKeyDown = (e) => {
    if (
      (e.key === "Enter" || e.key === "," || e.key === " ") &&
      inputValue.trim()
    ) {
      e.preventDefault();
      const username = extractUsernameFromString(inputValue);
      if (username) {
        onAddUsername(username);
      }
      setInputValue("");
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;

    if (value.endsWith(",") || value.endsWith(" ")) {
      const textBeforeSeparator = value.slice(0, -1).trim();
      if (textBeforeSeparator) {
        const username = extractUsernameFromString(textBeforeSeparator);
        if (username) {
          onAddUsername(username);
        }
        setInputValue("");
      }
    } else {
      setInputValue(value);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");

    if (pastedText) {
      addUsernamesFromText(pastedText);
      setInputValue("");
    }
  };

  return (
    <div className="audience-search__field">
      <div className="idh-label idh-label--with-tooltip">
        <IDHFormattedMessage
          id="front_search_by_social_login_topic"
          defaultMessage="Search by Social Login or Name"
        />
        <MaterialTooltip
          id="front_influencer_social_login_tooltip_1"
          content={
            <>
              <span className="audience-search__tooltip--white-bold">
                <IDHFormattedMessage
                  id="front_influencer_social_login_tooltip_1"
                  defaultMessage="Here you can find a specific creator by entering his nickname from the selected platform."
                />
              </span>
              <br />
              <br />
              <div>
                <span className="audience-search__tooltip--bold">
                  <IDHFormattedMessage
                    id="front_influencer_social_login_tooltip_2"
                    defaultMessage="Important: "
                  />
                </span>
                <IDHFormattedMessage
                  id="front_influencer_social_login_tooltip_3"
                  defaultMessage="When searching for a creator by entering its nickname from a given platform, you cannot use the other filters."
                />
              </div>
              <br />
              <br />
              <div>
                <span
                  className="audience-search__tooltip--bold"
                  style={{ marginRight: "4px" }}
                >
                  <IDHFormattedMessage
                    id="front_influencer_social_login_tooltip_4"
                    defaultMessage="Tip: "
                  />
                </span>
                <IDHFormattedMessage
                  id="front_influencer_social_login_tooltip_5"
                  defaultMessage='If you want to search for given username exactly, put it in quotes (")'
                />
              </div>
            </>
          }
        />
      </div>
      <div
        className={`audience-search__input ${isError ? "audience-search__input--error" : ""}`}
      >
        <div className="cdt-search-input">
          <input
            placeholder={intl.formatMessage({
              id: "front_search_by_social_login_placeholder",
              defaultMessage: "Search by social login or name",
            })}
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            data-cdt-input="influencer-username"
          />
        </div>
        {isError && (
          <div className="audience-search__input__error-message">
            <ErrorIcon />
            <IDHFormattedMessage
              id="front_incorrect_provider_error"
              defaultMessage="Incorrect link has been entered"
            />
          </div>
        )}
        {usernames.length > 0 && (
          <div style={{ marginTop: "15px" }}>
            <SelectedLabels
              values={usernames}
              unsetValue={onRemoveUsername}
              clearValues={onClearUsernames}
            />
          </div>
        )}
      </div>
    </div>
  );
}
